import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Clock, Mail, Maximize2, Minimize2, Save, Send, Trash2, X } from "lucide-react";
import { RichTextEditor } from "@/features/compose/RichTextEditor";
import { ComposeAttachments } from "@/features/compose/ComposeAttachments";
import { ComposeFileDropZone } from "@/features/compose/ComposeFileDropZone";
import {
  ComposeSignaturePreview,
} from "@/features/compose/ComposeSignaturePreview";
import { ComposeAccountSelect } from "@/features/compose/ComposeAccountSelect";
import { RecipientInput } from "@/features/contacts/RecipientInput";
import { ScheduleDateTimeModal } from "@/features/shared/ScheduleDateTimeModal";
import { MailLoadingState } from "@/features/shared/MailLoadingState";
import { useModalTransition } from "@/hooks/useModalTransition";
import {
  assembleComposeDraft,
  htmlToPlainText,
  migrateComposeDraftBody,
  hasComposeSendableContent,
  mergeLegacyComposeQuoteHtml,
  normalizeComposeOutboundBody,
  splitComposeBodyHtml,
} from "@/lib/compose/composeHtml";
import { readAccountSettings } from "@/lib/mail/accountSettingsPrefs";
import { getAccountDisplayLabel } from "@/lib/mail/accountDisplayLabel";
import {
  attachmentsForApi,
  composeAttachmentsLoading,
  filesToComposeAttachments,
} from "@/lib/compose/composeAttachments";
import { composeRecipientsFieldValid } from "@/lib/compose/composeRecipients";
import { isInlineComposeSession } from "@/lib/compose/composePlacement";
import type { OutboxEnqueuePayload } from "@/lib/outbox/outboxQueue";
import type { ComposeAttachment, ComposeDraft } from "@/lib/types/compose";
import type { MailAccountSummary } from "@/lib/types/mail";
import type { StoredComposeSession } from "@/lib/compose/composeSessionsStore";
import { clearComposeRecovery, readComposeRecovery, writeComposeRecovery } from "@/lib/compose/composeLocalRecovery";
import { cn } from "@/lib/cn";

const COMPOSE_DRAWER_TRANSITION_MS = 320;
const COMPOSE_EXPANDED_STORAGE_KEY = "mail-compose-drawer-expanded";

function readComposeExpandedPreference(): boolean {
  if (typeof sessionStorage === "undefined") return false;
  return sessionStorage.getItem(COMPOSE_EXPANDED_STORAGE_KEY) === "1";
}

type Props = {
  session: StoredComposeSession | null;
  accounts?: MailAccountSummary[];
  accountEmail?: string;
  /** Force relecture signature après sync / paramètres compte. */
  settingsVersion?: number;
  onUpdateDraft: (sessionId: string, patch: Partial<ComposeDraft>) => void;
  onSaveDraft: (sessionId: string, draft: ComposeDraft) => void;
  onDiscardDraft: (sessionId: string, draft: ComposeDraft) => void;
  onCloseSession: (sessionId: string) => void;
  onEnqueue: (sessionId: string, payload: OutboxEnqueuePayload) => void;
  onScheduleSend?: (sessionId: string, payload: OutboxEnqueuePayload, sendAt: string) => void;
  onChangeComposeAccount?: (sessionId: string, accountId: number) => void;
  /** Mobile : page pleine, sans bandeau du lecteur / de la liste au-dessus. */
  fullscreen?: boolean;
  /** Vrai tant que le serveur n'a pas accusé réception de cet envoi. */
  sending?: boolean;
};

const MODE_LABELS: Record<ComposeDraft["mode"], string> = {
  new: "Nouveau message",
  reply: "Répondre",
  replyAll: "Répondre à tous",
  forward: "Transférer",
};

const composeFieldClass =
  "w-full min-w-0 border-0 bg-transparent px-0 py-0 text-[0.8125rem] leading-snug text-[var(--mail-text)] placeholder:text-[var(--mail-muted)] focus:outline-none focus:ring-0 disabled:opacity-60";

export function ComposeDrawer({
  session,
  accounts = [],
  accountEmail,
  settingsVersion = 0,
  onUpdateDraft,
  onSaveDraft,
  onDiscardDraft,
  onCloseSession,
  onEnqueue,
  onScheduleSend,
  onChangeComposeAccount,
  fullscreen = false,
  sending = false,
}: Props) {
  const isInline = isInlineComposeSession(session);
  const open = session != null && !isInline;
  const { mounted, visible } = useModalTransition(open, COMPOSE_DRAWER_TRANSITION_MS);
  const [panelSession, setPanelSession] = useState<StoredComposeSession | null>(session);
  const [expanded, setExpanded] = useState(readComposeExpandedPreference);

  function toggleExpanded() {
    setExpanded((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem(COMPOSE_EXPANDED_STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore quota / private mode */
      }
      return next;
    });
  }

  useEffect(() => {
    if (session) setPanelSession(session);
  }, [session]);

  if (!mounted || isInline) return null;

  const displaySession = session ?? panelSession;
  if (!displaySession) return null;

  return createPortal(
    <div
      className={cn(
        "compose-dock",
        visible && "compose-dock-visible",
        fullscreen && "compose-dock--fullscreen"
      )}
      aria-live="polite"
    >
      {fullscreen ? null : (
        <div className="compose-backdrop pointer-events-auto" aria-hidden="true" />
      )}
      <div
        className={cn(
          "compose-drawer-wrap pointer-events-auto",
          expanded && "compose-drawer-wrap--expanded"
        )}
      >
        <ComposeDrawerPanel
          session={displaySession}
          accounts={accounts}
          accountEmail={accountEmail}
          settingsVersion={settingsVersion}
          expanded={expanded}
          fullscreen={fullscreen}
          onToggleExpanded={toggleExpanded}
          onUpdateDraft={onUpdateDraft}
          onSaveDraft={onSaveDraft}
          onDiscardDraft={onDiscardDraft}
          onCloseSession={onCloseSession}
          onEnqueue={onEnqueue}
          onScheduleSend={onScheduleSend}
          onChangeComposeAccount={onChangeComposeAccount}
          sending={sending}
        />
      </div>
    </div>,
    document.body
  );
}

function ComposeDrawerPanel({
  session,
  accounts = [],
  accountEmail,
  settingsVersion = 0,
  expanded,
  fullscreen = false,
  onToggleExpanded,
  onUpdateDraft,
  onSaveDraft,
  onDiscardDraft,
  onCloseSession,
  onEnqueue,
  onScheduleSend,
  onChangeComposeAccount,
  sending = false,
}: {
  session: StoredComposeSession;
  accounts?: MailAccountSummary[];
  accountEmail?: string;
  settingsVersion?: number;
  expanded: boolean;
  fullscreen?: boolean;
  onToggleExpanded: () => void;
  onUpdateDraft: (sessionId: string, patch: Partial<ComposeDraft>) => void;
  onSaveDraft: (sessionId: string, draft: ComposeDraft) => void;
  onDiscardDraft: (sessionId: string, draft: ComposeDraft) => void;
  onCloseSession: (sessionId: string) => void;
  onEnqueue: (sessionId: string, payload: OutboxEnqueuePayload) => void;
  onScheduleSend?: (sessionId: string, payload: OutboxEnqueuePayload, sendAt: string) => void;
  onChangeComposeAccount?: (sessionId: string, accountId: number) => void;
  sending?: boolean;
}) {
  const { id, draft, accountId } = session;
  const senderAccount = useMemo(
    () => accounts.find((entry) => entry.id === accountId),
    [accounts, accountId]
  );
  const senderLabel = senderAccount
    ? getAccountDisplayLabel(senderAccount)
    : accountEmail ?? "";
  const canPickAccount = draft.mode === "new" && accounts.length > 1 && onChangeComposeAccount != null;
  const normalized = migrateComposeDraftBody(draft);
  const draftEditorHtml = useMemo(() => {
    const parts = splitComposeBodyHtml(normalized.bodyHtml);
    return mergeLegacyComposeQuoteHtml(parts.messageHtml, draft.composeQuoteHtml);
  }, [draft.composeQuoteHtml, normalized.bodyHtml]);
  const [to, setTo] = useState(draft.to);
  const [cc, setCc] = useState(draft.cc);
  const [subject, setSubject] = useState(draft.subject);
  const [bodyHtml, setBodyHtml] = useState(draftEditorHtml);
  const [attachments, setAttachments] = useState<ComposeAttachment[]>(draft.attachments ?? []);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [showCc, setShowCc] = useState(Boolean(draft.cc) || draft.mode === "replyAll");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const bodyLoading = draft.bodyLoading === true;
  const richEditorValue = bodyLoading ? "" : bodyHtml || draftEditorHtml;
  // Ne pas inclure la longueur du HTML : sinon remount + perte de focus à chaque frappe.
  const richEditorKey = `${id}-${bodyLoading ? "loading" : "ready"}`;

  useLayoutEffect(() => {
    if (draft.bodyLoading) return;
    setBodyHtml(draftEditorHtml);
    setAttachments(draft.attachments ?? []);
  }, [draft.bodyLoading, draftEditorHtml, draft.attachments, id]);

  const recoveryApplied = useRef<string | null>(null);

  useEffect(() => {
    if (
      recoveryApplied.current !== id &&
      draft.mode === "new" &&
      !draft.to.trim() &&
      !draft.cc.trim() &&
      !draft.subject.trim() &&
      !htmlToPlainText(draft.bodyHtml || "").trim()
    ) {
      const saved = readComposeRecovery();
      if (saved) {
        recoveryApplied.current = id;
        setTo(saved.to);
        setCc(saved.cc);
        setSubject(saved.subject);
        setBodyHtml(saved.bodyHtml);
        setShowCc(Boolean(saved.cc.trim()));
        onUpdateDraft(id, {
          to: saved.to,
          cc: saved.cc,
          subject: saved.subject,
          bodyHtml: saved.bodyHtml,
          bodyText: htmlToPlainText(saved.bodyHtml),
        });
        return;
      }
    }
    recoveryApplied.current = id;
    setTo(draft.to);
    setCc(draft.cc);
    setSubject(draft.subject);
    setAttachmentError(null);
    setShowCc(Boolean(draft.cc) || draft.mode === "replyAll");
  }, [draft.to, draft.cc, draft.subject, draft.bodyHtml, draft.mode, id, onUpdateDraft]);

  const accountSignature = useMemo(
    () => (accountId ? readAccountSettings(accountId).signature : ""),
    [accountId, settingsVersion]
  );

  function sync(patch: Partial<ComposeDraft>) {
    onUpdateDraft(id, patch);
    const next = { ...currentDraft(), ...patch };
    writeComposeRecovery({
      to: next.to,
      cc: next.cc,
      subject: next.subject,
      bodyHtml: next.bodyHtml,
    });
  }

  function currentDraft(): ComposeDraft {
    return {
      ...draft,
      to,
      cc,
      subject,
      bodyHtml,
      bodyText: htmlToPlainText(bodyHtml),
      attachments,
    };
  }

  function outboundDraft() {
    return assembleComposeDraft(currentDraft(), accountSignature);
  }

  async function handleAddAttachments(files: FileList | File[]) {
    const list = files instanceof FileList ? [...files] : files;
    if (list.length === 0) return;
    const result = await filesToComposeAttachments(list, attachments);
    setAttachments(result.attachments);
    setAttachmentError(result.error ?? null);
    sync({ attachments: result.attachments });
  }

  function handleRemoveAttachment(fileId: string) {
    const next = attachments.filter((file) => file.id !== fileId);
    setAttachments(next);
    setAttachmentError(null);
    sync({ attachments: next });
  }

  function handleBodyChange(html: string) {
    setBodyHtml(html);
    sync({
      bodyHtml: html,
      bodyText: htmlToPlainText(html),
    });
  }

  function handleSaveDraft() {
    clearComposeRecovery();
    const assembled = outboundDraft();
    onSaveDraft(id, {
      ...currentDraft(),
      bodyHtml: assembled.bodyHtml,
      bodyText: assembled.bodyText,
    });
  }

  function handleDiscardDraft() {
    clearComposeRecovery();
    onDiscardDraft(id, currentDraft());
  }

  function closeKeepingWork() {
    const assembled = outboundDraft();
    const keep = Boolean(
      to.trim() || cc.trim() || subject.trim() || assembled.bodyText.trim() || attachments.length
    );
    if (!keep) {
      clearComposeRecovery();
      onCloseSession(id);
      return;
    }
    if (!canSave) return;
    handleSaveDraft();
  }

  function buildOutboundPayload(): OutboxEnqueuePayload | null {
    if (!accountId || !composeRecipientsFieldValid(to)) return null;
    const assembled = outboundDraft();
    const { bodyHtml: outHtml, bodyText: outText } = normalizeComposeOutboundBody(
      assembled.bodyHtml,
      assembled.bodyText
    );

    return {
      accountId,
      fromEmail: senderAccount?.email ?? accountEmail,
      to: to.trim(),
      cc: cc.trim() || undefined,
      subject,
      bodyText: outText,
      bodyHtml: outHtml,
      attachments: attachmentsForApi(attachments),
      inReplyTo: draft.inReplyTo,
      references: draft.references,
    };
  }

  function handleSend() {
    if (sending) return;
    const payload = buildOutboundPayload();
    if (!payload) return;
    clearComposeRecovery();
    onEnqueue(id, payload);
  }

  function handleConfirmSchedule(untilIso: string) {
    const payload = buildOutboundPayload();
    if (!payload || !onScheduleSend) return;
    clearComposeRecovery();
    onScheduleSend(id, payload, untilIso);
    setScheduleOpen(false);
  }

  const attachmentsBusy = composeAttachmentsLoading(attachments);
  const assembledPreview = outboundDraft();
  const hasContent = hasComposeSendableContent({
    subject,
    bodyHtml: assembledPreview.bodyHtml,
    bodyText: assembledPreview.bodyText,
    attachments,
  });
  const canSend =
    !sending &&
    composeRecipientsFieldValid(to) &&
    hasContent &&
    !attachmentsBusy &&
    !bodyLoading;
  const canSchedule = canSend && onScheduleSend != null;
  const canSave = !sending && !attachmentsBusy && !bodyLoading;
  const title = draft.draftUid ? "Modifier le brouillon" : MODE_LABELS[draft.mode];

  return (
    <ComposeFileDropZone
      className="compose-drawer-panel flex min-h-0 flex-col overflow-hidden"
      disabled={attachmentsBusy || bodyLoading}
      onAddFiles={handleAddAttachments}
    >
      <div className="compose-drawer-header shrink-0">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <div className="compose-drawer-icon" aria-hidden>
            <Mail className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="truncate text-sm font-semibold leading-tight text-[var(--mail-text)]">
                {title}
              </p>
              {draft.draftUid ? (
                <span className="compose-drawer-badge">Brouillon IMAP</span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {fullscreen ? null : (
          <button
            type="button"
            onClick={onToggleExpanded}
            className={cn(
              "compose-drawer-icon-btn compose-drawer-expand-btn",
              expanded && "compose-drawer-expand-btn--expanded"
            )}
            aria-label={expanded ? "Réduire la fenêtre de rédaction" : "Agrandir la fenêtre de rédaction"}
            aria-pressed={expanded}
          >
            <span className="compose-drawer-expand-btn__icon" aria-hidden>
              {expanded ? (
                <Minimize2 className="h-4 w-4 compose-drawer-expand-btn__glyph" />
              ) : (
                <Maximize2 className="h-4 w-4 compose-drawer-expand-btn__glyph" />
              )}
            </span>
          </button>
          )}
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={!canSave}
            className="compose-drawer-icon-btn"
            aria-label="Enregistrer en brouillon"
          >
            <Save className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleDiscardDraft}
            className={cn("compose-drawer-icon-btn", "compose-drawer-icon-btn-danger")}
            aria-label={draft.draftUid ? "Supprimer le brouillon" : "Fermer"}
            disabled={sending}
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={closeKeepingWork}
            className="compose-drawer-icon-btn"
            aria-label="Fermer"
            disabled={sending}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="compose-drawer-fields shrink-0">
        {canPickAccount ? (
          <ComposeAccountSelect
            accounts={accounts}
            accountId={accountId}
            onChange={(nextAccountId) => onChangeComposeAccount!(id, nextAccountId)}
          />
        ) : senderLabel ? (
          <div className="compose-field-row compose-field-row--readonly">
            <span className="compose-field-label">De</span>
            <span className="compose-field-value truncate">{senderLabel}</span>
          </div>
        ) : null}
        <div className="compose-field-row">
          <span className="compose-field-label">À</span>
          <RecipientInput
            label="À"
            value={to}
            placeholder="destinataire@domaine.fr"
            className={composeFieldClass}
            onChange={(next) => {
              setTo(next);
              sync({ to: next });
            }}
          />
          {!showCc && draft.mode !== "replyAll" ? (
            <button
              type="button"
              onClick={() => setShowCc(true)}
              className="compose-field-action"
            >
              Cc
            </button>
          ) : null}
        </div>

        {showCc || draft.mode === "replyAll" ? (
          <div className="compose-field-row">
            <span className="compose-field-label">Cc</span>
            <RecipientInput
              label="Cc"
              value={cc}
              placeholder="copie@domaine.fr"
              className={composeFieldClass}
              onChange={(next) => {
                setCc(next);
                sync({ cc: next });
              }}
            />
          </div>
        ) : null}

        <div className="compose-field-row">
          <span className="compose-field-label">Objet</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              sync({ subject: e.target.value });
            }}
            placeholder="Objet du message"
            className={composeFieldClass}
          />
        </div>
      </div>

      <div className="compose-drawer-body flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-2 pt-1.5">
        {bodyLoading ? (
          <div
            className="compose-drawer-loading flex min-h-[18rem] flex-1"
            aria-busy="true"
            aria-live="polite"
          >
            <MailLoadingState
              variant="message"
              label="Chargement du message à transférer…"
              hint="Récupération du contenu et des pièces jointes"
              layout="compact"
            />
          </div>
        ) : (
          <>
            <RichTextEditor
              key={richEditorKey}
              value={richEditorValue}
              onChange={handleBodyChange}
              placeholder="Écrivez votre message…"
              fillHeight
              className="compose-rich-editor-panel min-h-0 flex-1"
              onFilesDropped={handleAddAttachments}
            />
            <ComposeSignaturePreview signature={accountSignature} />
          </>
        )}
      </div>

      <ComposeAttachments
        attachments={attachments}
        onAddFiles={handleAddAttachments}
        onRemove={handleRemoveAttachment}
        error={attachmentError}
        suitePickerBackdrop="transparent"
      />

      <div className="compose-drawer-footer flex shrink-0 items-center justify-between gap-3">
        <p className={sending ? "text-[11px] text-[var(--mail-muted)]" : "hidden text-[11px] text-[var(--mail-muted)] sm:block"}>
          {sending
            ? "Transmission au serveur… La fenêtre se ferme quand le message est pris en charge."
            : "Brouillon enregistré sur le serveur IMAP"}
        </p>
          <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={!canSave}
            className="compose-drawer-btn-secondary mail-btn-secondary-gradient"
          >
            <Save className="h-4 w-4" />
            Brouillon
          </button>
          <div className="compose-drawer-send-split">
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              className="compose-drawer-btn-primary compose-drawer-btn-split-part"
            >
              <Send className="h-4 w-4" />
              {sending ? "Transmission…" : "Envoyer"}
            </button>
            <button
              type="button"
              onClick={() => setScheduleOpen(true)}
              disabled={!canSchedule}
              className="compose-drawer-btn-primary compose-drawer-btn-split-part compose-drawer-btn-split-right"
              aria-label="Programmer l'envoi"
            >
              <Clock className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <ScheduleDateTimeModal
        open={scheduleOpen}
        title="Programmer l'envoi"
        subject={subject.trim() || undefined}
        description="Choisissez quand envoyer ce message"
        confirmLabel="Programmer"
        previewPrefix="Envoi le"
        overlayBackdrop="transparent"
        onConfirm={handleConfirmSchedule}
        onClose={() => setScheduleOpen(false)}
      />
    </ComposeFileDropZone>
  );
}
